// A monthly membership renews indefinitely, so its real term and mileage are
// meaningless as filter values -- and its stored coverage_months is 1, which
// would collide with a genuine one-month plan. Every M2M option collapses into
// this single synthetic entry instead.
export const UNLIMITED_FILTER_LABEL = 'Unlimited - Renews Monthly'

export const isMonthlyMembershipOption = (option): boolean =>
  !!option?.monthly_membership

// The product_packages.discount column defaults to {} rather than NULL, and {}
// is truthy, so a plain `if (discount)` treats "no discount" as a discount:
// amount is undefined, the reduction comes out NaN, and numeral renders NaN as
// $0.00. Callers must ask this instead of testing the object for truthiness.
export const hasUsableDiscount = (discount): boolean =>
  !!discount && typeof discount.amount === 'number' && discount.amount > 0

export interface DownPaymentForTotalArgs {
  total: number
  isMonthlyMembership: boolean
  // The down payment already collected, when the package is paid.
  paidDownPayment?: number | null
  // The value in the down payment input, and whether the agent typed it.
  currentDownPayment?: number | null
  agentEdited: boolean
  defaultPct: number
  minPct: number
  // Share of the total the down payment may not exceed (0.75 for financed products).
  maxRatio: number
}

// The down payment to show when the total changes -- first load, or a Florida
// Discount/Reset step (blinker-legacy#164). A value the agent typed is kept and
// clamped into the new min/max rather than replaced by the default, as the
// Florida tier design requires (docs/blinker/florida-tiered-markup-plan.md).
export const downPaymentForTotal = ({
  total,
  isMonthlyMembership,
  paidDownPayment,
  currentDownPayment,
  agentEdited,
  defaultPct,
  minPct,
  maxRatio,
}: DownPaymentForTotalArgs): number => {
  // A monthly membership's down payment is the whole monthly price.
  if (isMonthlyMembership) return total
  if (paidDownPayment !== null && paidDownPayment !== undefined) {
    return Number(paidDownPayment)
  }

  if (agentEdited && currentDownPayment !== null && currentDownPayment !== undefined) {
    const min = (total * minPct) / 100
    const max = total * maxRatio
    return Math.min(Math.max(Number(currentDownPayment), min), max)
  }

  return (total * defaultPct) / 100
}

// 'YYYY-MM-DD' as local midnight. new Date('YYYY-MM-DD') is midnight UTC,
// which is the previous evening in the US.
const parseLocalDate = (value): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '')
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null
}

const addDays = (base: Date, days: number): Date => {
  const date = new Date(base)
  date.setDate(date.getDate() + days)
  return date
}

// The first and last day the agent may pick for the first payment. The server
// sends them (firstPaymentMinDate / firstPaymentMaxDate) because the calendar
// used to add the window days to the browser's clock, and a browser 627 weeks
// ahead posted first payments in 2038 (blinker-legacy#46, #47). today + days is
// only the fallback for a server that does not send the dates yet.
export const firstPaymentWindow = (
  productPackage,
  today: Date = new Date()
): { minDate: Date; maxDate: Date } => {
  const minDate = parseLocalDate(productPackage?.firstPaymentMinDate)
  const maxDate = parseLocalDate(productPackage?.firstPaymentMaxDate)
  if (minDate && maxDate) return { minDate, maxDate }

  return {
    minDate: addDays(today, Number(productPackage?.firstPaymentMinDays || 31)),
    maxDate: addDays(today, Number(productPackage?.firstPaymentMaxDays || 45)),
  }
}

interface Option {
  name: string
  price: number
}

interface Products<
  T extends Record<string, any>,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  K extends Record<string, any>
> {
  name: string
  options: T[]
}

export const selectedPrices = (selectedCoverageProducts, allProducts) => {
  if (allProducts.length === 0) {
    return []
  }

  const selectedProductAndOption = selectedCoverageProducts.selectedOptions.map(
    (selectedOption) => {
      const product = allProducts.find((p) => p.id == selectedOption.productId)
      const option = product?.options.find((o) => o.id == selectedOption.id)

      return { product, option }
    }
  )

  let prodPrices: Products<Option, { [key: string]: any }>[] = []

  selectedProductAndOption.forEach(({ product, option }) => {
    const { description } = product
    const {
      deductible,
      surcharges,
      coverage_months,
      coverage_miles,
      total_cost,
    } = option

    // A monthly membership renews indefinitely, so its stored term (1) and
    // mileage (999999) are never shown.
    let optionName = isMonthlyMembershipOption(option)
      ? `${deductible} - UNLIMITED months/UNLIMITED miles`
      : `${deductible} - ${coverage_months} months/${coverage_miles} miles`

    prodPrices.push({
      name: `${description}`,
      options: [
        {
          name: `${optionName}`,
          price: total_cost,
        },
      ],
    })

    selectedCoverageProducts.selectedSurcharges.forEach((surCode) => {
      surcharges.forEach((sur: any) => {
        if (sur.SurchargeCode === surCode) {
          const productFound = prodPrices.find((p) => p.name === description)
          productFound &&
            productFound.options.push({
              name: sur.Description,
              price: sur.Price,
            })
        }
      })
    })
  })

  return prodPrices
}

export const getProductFilters = (products) => {
  let hasMonthlyMembership = false

  const filters = products.reduce(
    (acc, product) => {
      product.options.forEach((option) => {
        if (isMonthlyMembershipOption(option)) {
          // Deliberately not pushed into the numeric lists: sorting would place
          // months 1 first and miles 999999 last, splitting one concept across
          // opposite ends of the two filters.
          hasMonthlyMembership = true
        } else {
          if (!acc.coverage_miles.includes(option.coverage_miles)) {
            acc.coverage_miles.push(option.coverage_miles)
          }
          if (!acc.coverage_months.includes(option.coverage_months)) {
            acc.coverage_months.push(option.coverage_months)
          }
        }
        if (!acc.deductibles.includes(option.deductible)) {
          acc.deductibles.push(option.deductible)
        }
        option.surcharges.map((sur) => {
          if (!acc.surcharges.includes(sur.Description)) {
            acc.surcharges.push(sur.Description)
            acc.surchargesData.push(sur)
          }
        })
      })
      return acc
    },
    {
      deductibles: [],
      coverage_miles: [],
      coverage_months: [],
      surcharges: [],
      surchargesData: [],
    }
  )

  // Prepended after sorting, never sorted into place -- the synthetic entry has
  // no numeric value to sort by, and relying on one is the coupling the plan's
  // keystone decision exists to avoid. First in both lists, matching the way
  // monthly membership plans sort first in the results.
  const withUnlimited = (values: any[]) =>
    hasMonthlyMembership ? [UNLIMITED_FILTER_LABEL, ...values] : values

  const sortedFilters = {
    deductibles: filters.deductibles.sort((a, b) => a.localeCompare(b)),
    coverage_months: withUnlimited(
      filters.coverage_months.sort((a: number, b: number) => a - b)
    ),
    coverage_miles: withUnlimited(
      filters.coverage_miles.sort((a: number, b: number) => a - b)
    ),
    surcharges: filters.surcharges.sort((a, b) => a.localeCompare(b)),
    surchargesData: filters.surchargesData.sort((a, b) =>
      a.Description.localeCompare(b.Description)
    ),
  }
  return sortedFilters
}
